/**
 * Risk Register JavaScript
 *
 * Handles all risk register functionality including:
 * - DataTables initialization
 * - Add/Edit/Delete risk modals
 * - Threat category loading from API
 * - Overall threat rating calculation
 */

(function($) {
    'use strict';

    // Store simple-datatables instance
    let risksTable = null;
    let threatCategoriesLoaded = false;
    // Chart.js instances: active (Open, Under Review), resolved (Closed, Archived), business unit
    let riskChartInstanceActive = null;
    let riskChartInstanceResolved = null;
    let riskChartInstanceBusinessUnit = null;

    /** Status order for X axis; rating order for stack segments (bottom to top) */
    var RISK_STATUS_ORDER = ['Open', 'Under Review', 'Closed', 'Archived'];
    var RISK_STATUS_ACTIVE = ['Open', 'Under Review'];
    var RISK_STATUS_RESOLVED = ['Closed', 'Archived'];
    var RISK_RATING_ORDER = ['Low', 'Moderate', 'High', 'Critical'];
    var RISK_BUSINESS_UNIT_ORDER = [
        'Finance & Accounting',
        'Human Resources (HR)',
        'Legal & Compliance',
        'Sales & Business Development',
        'Marketing & Communications',
        'Technology & Cybersecurity',
        'Facilities & Physical Security',
        'Other'
    ];
    /** Colors from threat severity/likelihood gradient (4-level scale) */
    var RISK_RATING_COLORS = {
        'Low': 'rgba(22, 163, 74, 0.9)',       /* #16a34a */
        'Moderate': 'rgba(217, 119, 6, 0.9)',  /* #d97706 */
        'High': 'rgba(234, 88, 12, 0.9)',      /* #ea580c */
        'Critical': 'rgba(220, 38, 38, 0.9)'   /* #dc2626 */
    };

    /**
     * Initialize the risk register functionality
     */
    function init() {
        initDataTable();
        initRiskViewToggle();
        initNewRiskForm();
        initEditRiskForm();
        initDeleteRisk();
        initFilters();
        initModalEvents();
    }

    /**
     * Hide .risk-register-header when the user has no risks (empty table).
     */
    function toggleRiskRegisterHeader() {
        var $container = $('#risk-table-container');
        var hasRisks = $container.find('#userRisksTable tbody tr').length > 0;
        $container.find('.risk-register-header').toggleClass('d-none', !hasRisks);
    }

    /**
     * Hide the risk table loader and show table content (same pattern as evidence page)
     */
    function hideRiskTableLoader() {
        var $loader = $('#risk-table-loader');
        var $view = $('#risk-table-view');
        if ($loader.length) {
            $loader.addClass('d-none');
        }
        if ($view.length) {
            $view.removeClass('risk-table-view-loading');
        }
    }

    /**
     * Show the Add Risk modal loader (overlay until Select2 and form are ready)
     */
    function showNewRiskModalLoader() {
        var $loader = $('#new-risk-modal-loader');
        if ($loader.length) {
            $loader.removeClass('d-none');
        }
    }

    /**
     * Hide the Add Risk modal loader
     */
    function hideNewRiskModalLoader() {
        var $loader = $('#new-risk-modal-loader');
        if ($loader.length) {
            $loader.addClass('d-none');
        }
    }

    /**
     * Initialize simple-datatables for the risks table (same library as evidence table)
     */
    function initDataTable() {
        var table = document.querySelector('#userRisksTable');
        if (!table) {
            hideRiskTableLoader();
            toggleRiskRegisterHeader();
            return;
        }

        // Don't reinit if already wrapped
        if (table.closest('.datatable-wrapper')) {
            hideRiskTableLoader();
            return;
        }

        // Find DataTable constructor (same approach as evidence-table-init.js)
        var DataTableClass = null;
        if (typeof DataTable !== 'undefined') {
            DataTableClass = DataTable;
        } else if (typeof simpleDatatables !== 'undefined' && simpleDatatables.DataTable) {
            DataTableClass = simpleDatatables.DataTable;
        } else if (typeof window.DataTable !== 'undefined') {
            DataTableClass = window.DataTable;
        }

        if (!DataTableClass) {
            console.error('[Risk Register] simple-datatables DataTable class not found');
            hideRiskTableLoader();
            return;
        }

        try {
            risksTable = new DataTableClass(table, {
                sortable: true,
                paging: true,
                perPage: 25,
                perPageSelect: false,
                searchable: true,
                columns: [
                    {
                        select: 0,
                        sort: 'asc' // Default sort by Name
                    },
                    {
                        select: 1, // Business Unit
                        headerClass: 'text-center',
                        cellClass: 'text-center'
                    },
                    {
                        select: 2, // Threat Rating
                        headerClass: 'text-center',
                        cellClass: 'text-center'
                    },
                    {
                        select: 3, // Status
                        headerClass: 'text-center',
                        cellClass: 'text-center'
                    },
                    {
                        select: 4, // Expires
                        headerClass: 'text-center',
                        cellClass: 'text-center'
                    },
                    {
                        select: 5, // Actions
                        sortable: false,
                        searchable: false,
                        headerClass: 'risk-actions-header text-center',
                        cellClass: 'text-center'
                    }
                ],
                labels: {
                    placeholder: 'Search:',
                    noRows: 'No risks found',
                    info: 'Showing {start} to {end} of {rows} entries'
                }
            });

            // Add our own search input (form-control) in header left, wire to DataTable search API; hide datatable-top (projects-style layout)
            setTimeout(function() {
                var tableWrapper = $(table).closest('.datatable-wrapper');
                if (!tableWrapper.length) {
                    tableWrapper = $('.datatable-wrapper').has('#userRisksTable');
                }
                var topContainer = tableWrapper.find('.datatable-top');
                var headerLeft = $('#risk-table-view .risk-header-left');
                var headerRight = $('#risk-table-view .risk-header-right');

                // Add search input with form-control (like projects page) and drive table search via API
                if (headerLeft.length && risksTable && !headerLeft.find('.risk-table-search-input').length) {
                    var searchInput = $('<input type="search" class="form-control risk-table-search-input" placeholder="Search:" title="Search within table" aria-controls="userRisksTable">');
                    searchInput.prependTo(headerLeft);
                    searchInput.on('input keyup', function() {
                        var term = $(this).val();
                        if (typeof risksTable.search === 'function') {
                            risksTable.search(term);
                        }
                    });
                }

                // Move Add New Risk button into table view header right only (chart view gets it when switching)
                var newRiskBtn = $('.new-project-container .new-project-btn');
                if (newRiskBtn.length && headerRight.length) {
                    newRiskBtn.detach();
                    var buttonContainer = $('<div class="new-project-btn-container"></div>');
                    buttonContainer.append(newRiskBtn);
                    headerRight.append(buttonContainer);
                    $('.new-project-container').hide();
                }

                // Hide datatable-top so only our risk-register-header controls the layout
                if (topContainer.length) {
                    topContainer.hide();
                }

                // If chart view is visible (e.g. saved preference), move button to chart header
                var chartViewEl = document.getElementById('risk-chart-view');
                if (chartViewEl && chartViewEl.style.display !== 'none') {
                    var c = $('.new-project-btn-container');
                    if (c.length) c.appendTo($('#risk-chart-view .risk-header-right'));
                }

                hideRiskTableLoader();
                toggleRiskRegisterHeader();
            }, 100);

            console.log('[Risk Register] simple-datatables initialized');
        } catch (error) {
            console.error('[Risk Register] Error initializing DataTable:', error);
            hideRiskTableLoader();
        }
    }

    /**
     * Initialize checkbox-based status filter (same pattern as projects table)
     */
    function initFilters() {
        var statusCheckboxes = $('.risk-status-checkbox');
        var statusAllCheckbox = $('#risk-status-filter-all');

        function applyStatusFilter() {
            var selectedStatuses = [];
            statusCheckboxes.filter(':checked').each(function() {
                selectedStatuses.push($(this).val());
            });

            var table = document.querySelector('#userRisksTable');
            if (!table) return;

            var rows = table.querySelectorAll('tbody tr');
            rows.forEach(function(row) {
                var cells = row.querySelectorAll('td');
                if (cells.length < 4) return;

                // Column 3 = Status (0-indexed)
                var rowStatus = (cells[3].textContent || '').trim();

                var show = selectedStatuses.length === statusCheckboxes.length || selectedStatuses.indexOf(rowStatus) !== -1;
                row.style.display = show ? '' : 'none';
            });
        }

        statusAllCheckbox.on('change', function(e) {
            e.stopPropagation();
            statusCheckboxes.prop('checked', $(this).is(':checked'));
            applyStatusFilter();
        });

        statusCheckboxes.on('change', function(e) {
            e.stopPropagation();
            statusAllCheckbox.prop('checked', statusCheckboxes.filter(':checked').length === statusCheckboxes.length);
            applyStatusFilter();
        });

        // Prevent dropdown from closing when clicking inside
        $('.risk-filter-container .dropdown-menu').on('click', function(e) {
            e.stopPropagation();
        });
    }

    /**
     * Risk view toggle: switch between table view and chart view.
     * Uses event delegation so buttons still work after table refresh (replaced DOM).
     */
    function initRiskViewToggle() {
        var tableView = document.getElementById('risk-table-view');
        var chartView = document.getElementById('risk-chart-view');

        if (!tableView || !chartView) {
            return;
        }

        function updateButtonStates(showTable) {
            var tableBtns = document.querySelectorAll('#risk-table-view-btn');
            var chartBtns = document.querySelectorAll('#risk-chart-view-btn');
            tableBtns.forEach(function(b) { b.classList.toggle('active', showTable); });
            chartBtns.forEach(function(b) { b.classList.toggle('active', !showTable); });
        }

        function showRiskTableView() {
            tableView.style.display = 'block';
            chartView.style.display = 'none';
            updateButtonStates(true);
            moveAddRiskButtonToView('table');
        }

        function showRiskChartView() {
            tableView.style.display = 'none';
            chartView.style.display = 'block';
            updateButtonStates(false);
            moveAddRiskButtonToView('chart');
            ensureRiskChart();
        }

        function moveAddRiskButtonToView(view) {
            var container = $('.new-project-btn-container');
            if (!container.length) return;
            var target = view === 'chart' ? $('#risk-chart-view .risk-header-right') : $('#risk-table-view .risk-header-right');
            if (target.length) {
                container.appendTo(target);
            }
        }

        var savedView = (typeof localStorage !== 'undefined' && localStorage.getItem('riskRegisterViewPreference')) || 'table';
        if (savedView === 'chart') {
            showRiskChartView();
        } else {
            showRiskTableView();
        }

        $(document).on('click', '#risk-table-view-btn', function() {
            showRiskTableView();
            if (typeof localStorage !== 'undefined') {
                localStorage.setItem('riskRegisterViewPreference', 'table');
            }
        });
        $(document).on('click', '#risk-chart-view-btn', function() {
            showRiskChartView();
            if (typeof localStorage !== 'undefined') {
                localStorage.setItem('riskRegisterViewPreference', 'chart');
            }
        });
    }

    /**
     * Build datasets for a subset of statuses from window.riskChartData
     */
    function buildChartDataForStatuses(data, statusList) {
        var labels = statusList.filter(function(s) { return data[s] !== undefined; });
        if (labels.length === 0) labels = statusList.slice();

        var datasets = RISK_RATING_ORDER.map(function(rating) {
            var values = labels.map(function(status) {
                var row = data[status];
                return (row && row[rating] !== undefined) ? row[rating] : 0;
            });
            return {
                label: rating,
                data: values,
                backgroundColor: RISK_RATING_COLORS[rating] || 'rgba(128, 128, 128, 0.9)',
                stack: 'stack0'
            };
        });
        return { labels: labels, datasets: datasets };
    }

    /**
     * Build datasets for business unit chart from window.riskBusinessUnitChartData
     */
    function buildChartDataForBusinessUnits(data) {
        var labels = RISK_BUSINESS_UNIT_ORDER.filter(function(bu) { return data[bu] !== undefined; });
        Object.keys(data).forEach(function(bu) {
            if (labels.indexOf(bu) === -1) {
                labels.push(bu);
            }
        });
        if (labels.length === 0) {
            labels = RISK_BUSINESS_UNIT_ORDER.slice();
        }

        var datasets = RISK_RATING_ORDER.map(function(rating) {
            var values = labels.map(function(bu) {
                var row = data[bu];
                return (row && row[rating] !== undefined) ? row[rating] : 0;
            });
            return {
                label: rating,
                data: values,
                backgroundColor: RISK_RATING_COLORS[rating] || 'rgba(128, 128, 128, 0.9)',
                stack: 'stack0'
            };
        });

        return { labels: labels, datasets: datasets };
    }

    /**
     * Common chart options for both risk charts (onClick, scales, legend, tooltip)
     */
    function getRiskChartOptions() {
        return {
            responsive: true,
            maintainAspectRatio: true,
            aspectRatio: 1.6,
            onClick: function(evt, elements, chart) {
                if (elements.length === 0) return;
                var el = elements[0];
                var statusIndex = el.index;
                var ratingIndex = el.datasetIndex;
                var status = chart.data.labels[statusIndex];
                var rating = chart.data.datasets[ratingIndex].label;
                var count = chart.data.datasets[ratingIndex].data[statusIndex];
                if (count > 0) {
                    openRiskChartSegmentModal(status, rating);
                }
            },
            scales: {
                x: {
                    stacked: true,
                    title: { display: true, text: 'Risk Status' }
                },
                y: {
                    stacked: true,
                    beginAtZero: true,
                    ticks: { stepSize: 1 },
                    title: { display: true, text: 'Number of risks' }
                }
            },
            plugins: {
                legend: {
                    display: true,
                    position: 'right',
                    labels: {
                        sort: function(a, b) {
                            var order = { 'Critical': 0, 'High': 1, 'Moderate': 2, 'Low': 3 };
                            return (order[a.text] ?? 4) - (order[b.text] ?? 4);
                        }
                    }
                },
                tooltip: {
                    callbacks: {
                        label: function(context) {
                            var count = context.parsed.y;
                            var label = context.dataset.label || '';
                            return label + ': ' + count + ' risk' + (count !== 1 ? 's' : '');
                        }
                    }
                }
            }
        };
    }

    /**
     * Chart options for business unit × threat rating stacked chart
     */
    function getRiskBusinessUnitChartOptions() {
        return {
            responsive: true,
            maintainAspectRatio: false,
            layout: {
                padding: {
                    bottom: 4
                }
            },
            onClick: function(evt, elements, chart) {
                if (elements.length === 0) return;
                var el = elements[0];
                var businessUnit = chart.data.labels[el.index];
                var rating = chart.data.datasets[el.datasetIndex].label;
                var count = chart.data.datasets[el.datasetIndex].data[el.index];
                if (count > 0) {
                    openRiskBusinessUnitChartSegmentModal(businessUnit, rating);
                }
            },
            scales: {
                x: {
                    stacked: true,
                    offset: true,
                    grid: { offset: true },
                    ticks: {
                        maxRotation: 45,
                        minRotation: 45,
                        autoSkip: false,
                        padding: 4
                    }
                },
                y: {
                    stacked: true,
                    beginAtZero: true,
                    ticks: { stepSize: 1 },
                    title: { display: true, text: 'Number of risks' }
                }
            },
            plugins: {
                legend: {
                    display: true,
                    position: 'right',
                    labels: {
                        sort: function(a, b) {
                            var order = { 'Critical': 0, 'High': 1, 'Moderate': 2, 'Low': 3 };
                            return (order[a.text] ?? 4) - (order[b.text] ?? 4);
                        }
                    }
                },
                tooltip: {
                    callbacks: {
                        label: function(context) {
                            var count = context.parsed.y;
                            var label = context.dataset.label || '';
                            return label + ': ' + count + ' risk' + (count !== 1 ? 's' : '');
                        }
                    }
                }
            }
        };
    }

    /**
     * Build or update all stacked bar charts from window.riskChartData / riskBusinessUnitChartData
     */
    function ensureRiskChart() {
        if (typeof Chart === 'undefined') return;

        function createOrUpdateChart(canvasId, chartData, existingInstance, options) {
            var canvas = document.getElementById(canvasId);
            if (!canvas) return null;

            if (existingInstance) {
                existingInstance.data.labels = chartData.labels;
                existingInstance.data.datasets = chartData.datasets;
                existingInstance.update();
                return existingInstance;
            }

            var ctx = canvas.getContext('2d');
            return new Chart(ctx, {
                type: 'bar',
                data: { labels: chartData.labels, datasets: chartData.datasets },
                options: options
            });
        }

        var data = window.riskChartData;
        if (data && typeof data === 'object') {
            var activeData = buildChartDataForStatuses(data, RISK_STATUS_ACTIVE);
            var resolvedData = buildChartDataForStatuses(data, RISK_STATUS_RESOLVED);
            var statusOptions = getRiskChartOptions();
            riskChartInstanceActive = createOrUpdateChart(
                'riskRegisterChartActive', activeData, riskChartInstanceActive, statusOptions
            );
            riskChartInstanceResolved = createOrUpdateChart(
                'riskRegisterChartResolved', resolvedData, riskChartInstanceResolved, statusOptions
            );
        }

        var buData = window.riskBusinessUnitChartData;
        if (buData && typeof buData === 'object') {
            var businessUnitChartPayload = buildChartDataForBusinessUnits(buData);
            riskChartInstanceBusinessUnit = createOrUpdateChart(
                'riskRegisterChartBusinessUnit',
                businessUnitChartPayload,
                riskChartInstanceBusinessUnit,
                getRiskBusinessUnitChartOptions()
            );
        }
    }

    /**
     * Open modal listing risks for a chart segment (status + rating). Fetches list via AJAX.
     */
    function openRiskChartSegmentModal(status, rating) {
        var $modal = $('#riskChartSegmentModal');
        var $title = $modal.find('#riskChartSegmentModalLabel');
        var $loading = $('#riskChartSegmentModalLoading');
        var $list = $('#riskChartSegmentModalList');
        var $empty = $('#riskChartSegmentModalEmpty');

        if (!$modal.length) return;

        $title.text('Risks: ' + status + ' – ' + rating);
        $list.empty().hide();
        $empty.hide().text('No risks in this category.');
        $loading.show();

        var modalEl = document.getElementById('riskChartSegmentModal');
        var modal = bootstrap.Modal.getInstance(modalEl) || new bootstrap.Modal(modalEl);
        modal.show();

        $.ajax({
            url: riskRegisterData.ajax_url,
            type: 'POST',
            data: {
                action: 'get_risks_by_status_rating',
                nonce: riskRegisterData.nonce,
                status: status,
                rating: rating
            },
            success: function(response) {
                $loading.hide();
                if (response.success && response.data.risks && response.data.risks.length > 0) {
                    response.data.risks.forEach(function(r) {
                        var $a = $('<a class="list-group-item list-group-item-action">')
                            .attr('href', r.url || '#')
                            .text(r.risk_title || r.risk_id);
                        $list.append($a);
                    });
                    $list.show();
                } else {
                    $empty.show();
                }
            },
            error: function() {
                $loading.hide();
                $empty.text('Unable to load risks.').show();
            }
        });
    }

    /**
     * Open modal listing risks for a business unit chart segment. Fetches list via AJAX.
     */
    function openRiskBusinessUnitChartSegmentModal(businessUnit, rating) {
        var $modal = $('#riskChartSegmentModal');
        var $title = $modal.find('#riskChartSegmentModalLabel');
        var $loading = $('#riskChartSegmentModalLoading');
        var $list = $('#riskChartSegmentModalList');
        var $empty = $('#riskChartSegmentModalEmpty');

        if (!$modal.length) return;

        $title.text('Risks: ' + businessUnit + ' – ' + rating);
        $list.empty().hide();
        $empty.hide().text('No risks in this category.');
        $loading.show();

        var modalEl = document.getElementById('riskChartSegmentModal');
        var modal = bootstrap.Modal.getInstance(modalEl) || new bootstrap.Modal(modalEl);
        modal.show();

        $.ajax({
            url: riskRegisterData.ajax_url,
            type: 'POST',
            data: {
                action: 'get_risks_by_business_unit_rating',
                nonce: riskRegisterData.nonce,
                businessUnit: businessUnit,
                rating: rating
            },
            success: function(response) {
                $loading.hide();
                if (response.success && response.data.risks && response.data.risks.length > 0) {
                    response.data.risks.forEach(function(r) {
                        var $a = $('<a class="list-group-item list-group-item-action">')
                            .attr('href', r.url || '#')
                            .text(r.risk_title || r.risk_id);
                        $list.append($a);
                    });
                    $list.show();
                } else {
                    $empty.show();
                }
            },
            error: function() {
                $loading.hide();
                $empty.text('Unable to load risks.').show();
            }
        });
    }

    /**
     * Update chart data (e.g. after table refresh) and refresh chart if visible
     */
    function updateRiskChartData(chartData, businessUnitChartData) {
        if (chartData) {
            window.riskChartData = chartData;
        }
        if (businessUnitChartData) {
            window.riskBusinessUnitChartData = businessUnitChartData;
        }
        var chartView = document.getElementById('risk-chart-view');
        if (chartView && chartView.style.display !== 'none') {
            ensureRiskChart();
        }
    }

    /**
     * Initialize modal events for loading threat categories
     */
    function initModalEvents() {
        // Load threat categories when new risk modal opens; show loader until Select2 is ready
        $('#newRiskModal').on('show.bs.modal', function() {
            showNewRiskModalLoader();
            loadThreatCategories('#threatCategories', function() {
                hideNewRiskModalLoader();
            });
        });

        // Init Risk Owner Select2 once the new risk modal is fully visible (so
        // dropdownParent measurements are correct inside the Bootstrap modal).
        $('#newRiskModal').on('shown.bs.modal', function() {
            initRiskOwnerSelect2($('#riskOwner'));
            initRiskOwnerSelect2($('#riskAdvisor'));
        });

        // Destroy Select2 on modal close to prevent DOM issues on re-open; reset loader for next open
        $('#newRiskModal').on('hidden.bs.modal', function() {
            var $select = $('#threatCategories');
            if ($select.length && $select.hasClass('select2-hidden-accessible')) {
                $select.select2('destroy');
            }
            // Re-mark as needing Select2 init on next open
            $select.removeData('select2Initialized');
            destroyRiskOwnerSelect2($('#riskOwner'));
            destroyRiskOwnerSelect2($('#riskAdvisor'));
            showNewRiskModalLoader();
        });

        $('#editRiskModal').on('hidden.bs.modal', function() {
            var $select = $('#editThreatCategories');
            if ($select.length && $select.hasClass('select2-hidden-accessible')) {
                $select.select2('destroy');
            }
            $select.removeData('select2Initialized');
            destroyRiskOwnerSelect2($('#editRiskOwner'));
            destroyRiskOwnerSelect2($('#editRiskAdvisor'));
        });

        // Load threat categories when edit modal opens (handled separately in populateEditRiskForm)
    }

    /**
     * Load threat categories from the local threat explorer JSON
     * @param {string} selectSelector - jQuery selector for the select element
     * @param {Function} callback - Optional callback after loading
     */
    function loadThreatCategories(selectSelector, callback) {
        const $select = $(selectSelector);

        if (!$select.length) {
            if (callback) callback();
            return;
        }

        // Check if already loaded for this select - just re-init Select2
        if ($select.data('loaded')) {
            initThreatCategorySelect2($select);
            if (callback) callback();
            return;
        }

        const currentYear = new Date().getFullYear();

        $.ajax({
            url: riskRegisterData.ajax_url,
            type: 'POST',
            data: {
                action: 'get_threat_categories_for_risk',
                nonce: riskRegisterData.nonce,
                year: currentYear
            },
            success: function(response) {
                if (response.success && response.data && response.data.threats) {
                    const threats = response.data.threats;

                    // Clear and populate
                    $select.empty();

                    threats.forEach(function(threat) {
                        const threatId = threat.threatid || '';
                        const threatName = threat.threatname || '';
                        const threatCategory = threat.threatcategory || '';

                        if (threatId) {
                            $select.append(
                                $('<option></option>')
                                    .val(threatId)
                                    .text(threatId + ': ' + threatName)
                                    .attr('data-category', threatCategory)
                            );
                        }
                    });

                    console.log('Loaded ' + threats.length + ' threats into select');
                    $select.data('loaded', true);

                    // Initialize Select2 with search on the threat categories select
                    initThreatCategorySelect2($select);
                } else {
                    console.warn('No threats found in response:', response);
                }

                if (callback) callback();
            },
            error: function(xhr, status, error) {
                console.error('Error loading threat categories:', error);
                console.error('Response:', xhr.responseText);
                if (callback) callback();
            }
        });
    }

    /**
     * Initialize Select2 on the Risk Owner picker. Mirrors the corporate user
     * picker on the Asset modal (Asset Owner / Risk Advisor).
     * @param {jQuery} $select - The Risk Owner select element
     */
    function initRiskOwnerSelect2($select) {
        if (!$select || !$select.length || !$.fn.select2) return;

        var $modal = $select.closest('.modal');
        var dropdownParent = $modal.length ? $modal : $(document.body);

        if ($select.hasClass('select2-hidden-accessible')) {
            $select.select2('destroy');
        }

        var isMulti = $select.prop('multiple');
        var opts = {
            width: '100%',
            dropdownParent: dropdownParent
        };
        if (isMulti) {
            opts.closeOnSelect = false;
            opts.placeholder = 'Select options';
        } else {
            opts.allowClear = !$select.prop('required');
            opts.placeholder = 'Select';
        }
        $select.select2(opts);

        $select.off('select2:open.riskOwnerS2').on('select2:open.riskOwnerS2', function() {
            setTimeout(function() {
                var el = document.querySelector('.select2-container--open .select2-search__field');
                if (el) el.focus();
            }, 0);
        });
    }

    /**
     * Tear down Select2 on the Risk Owner picker (modal-safe).
     * @param {jQuery} $select - The Risk Owner select element
     */
    function destroyRiskOwnerSelect2($select) {
        if (!$select || !$select.length || !$.fn.select2) return;
        $select.off('select2:open.riskOwnerS2');
        if ($select.hasClass('select2-hidden-accessible')) {
            $select.select2('destroy');
        }
    }

    /**
     * If the stored owner value is not in the corporate user list (legacy data:
     * display name or email), append it as an option so Select2 can render it.
     * @param {jQuery} $select - The Risk Owner select element
     * @param {string} value - The stored value to apply
     */
    function ensureRiskOwnerSelectOption($select, value) {
        if (!$select || !$select.length) return;
        var values;
        if (Array.isArray(value)) {
            values = value.slice();
        } else if (value === null || value === undefined || value === '') {
            values = [];
        } else {
            values = String(value).split(',').map(function(v) { return v.trim(); }).filter(Boolean);
        }
        values.forEach(function(v) {
            v = String(v);
            var found = false;
            $select.find('option').each(function() {
                if ($(this).attr('value') === v) { found = true; return false; }
            });
            if (!found) {
                $select.append($('<option/>').attr('value', v).text(v));
            }
        });
        if ($select.prop('multiple')) {
            $select.val(values);
        } else {
            $select.val(values[0] || '');
        }
    }

    /**
     * Initialize Select2 on a threat category select element
     * @param {jQuery} $select - The select element jQuery object
     */
    function initThreatCategorySelect2($select) {
        if (!$.fn.select2) {
            console.warn('[Risk Register] Select2 not available');
            return;
        }

        // Determine the parent modal for proper dropdown positioning
        var $modal = $select.closest('.modal');
        var select2Options = {
            placeholder: 'Search and select threat categories...',
            allowClear: true,
            width: '100%',
            closeOnSelect: false
        };

        // Set dropdownParent to the modal so the dropdown renders inside it
        if ($modal.length) {
            select2Options.dropdownParent = $modal;
        }

        // Destroy existing Select2 instance if any, then reinitialize
        if ($select.hasClass('select2-hidden-accessible')) {
            $select.select2('destroy');
        }

        $select.select2(select2Options);

        // Fix: Bootstrap modal tabindex="-1" steals focus from Select2 search input.
        // Force focus to the search field whenever the Select2 dropdown opens.
        $select.on('select2:open', function() {
            var searchField = document.querySelector('.select2-container--open .select2-search__field');
            if (searchField) {
                searchField.focus();
            }
        });
    }

    /**
     * Normalize legacy 5-level rating to 4-level for dropdowns (edit form).
     */
    function normalizeRatingForSelect(rating) {
        if (!rating) return '';
        var map = { 'Very Low': 'Low', 'Very High': 'Critical' };
        return map[rating] || rating;
    }

    /**
     * Calculate overall threat rating from likelihood and severity.
     * Averages the two numeric values and rounds up, then maps to rating label.
     * @param {string} likelihood - Threat likelihood value
     * @param {string} severity - Threat severity value
     * @returns {object} Object with rating and badge class
     */
    function calculateOverallRating(likelihood, severity) {
        if (!likelihood || !severity) {
            return { rating: '', badgeClass: 'bg-secondary text-white', display: 'Select likelihood and severity' };
        }

        const ratingValues = {
            'Low': 1,
            'Moderate': 2,
            'High': 3,
            'Critical': 4,
            'Very Low': 1,
            'Very High': 4
        };

        const numToRating = {
            1: 'Low',
            2: 'Moderate',
            3: 'High',
            4: 'Critical'
        };

        const numToBadgeClass = {
            1: 'bg-rating-low text-white',
            2: 'bg-rating-moderate text-white',
            3: 'bg-rating-high text-white',
            4: 'bg-rating-critical text-white'
        };

        const likelihoodNum = ratingValues[likelihood] || 2;
        const severityNum = ratingValues[severity] || 2;
        const average = (likelihoodNum + severityNum) / 2;
        const roundedUp = Math.min(4, Math.max(1, Math.ceil(average)));
        const rating = numToRating[roundedUp];
        const badgeClass = numToBadgeClass[roundedUp];

        return { rating: rating, badgeClass: badgeClass, display: rating };
    }

    /**
     * Update overall rating display
     * @param {string} likelihoodSelector - Selector for likelihood select
     * @param {string} severitySelector - Selector for severity select
     * @param {string} displaySelector - Selector for display element
     * @param {string} inputSelector - Selector for hidden input
     */
    function updateOverallRatingDisplay(likelihoodSelector, severitySelector, displaySelector, inputSelector) {
        const likelihood = $(likelihoodSelector).val();
        const severity = $(severitySelector).val();
        const result = calculateOverallRating(likelihood, severity);

        $(displaySelector).html('<span class="badge ' + result.badgeClass + '">' + result.display + '</span>');
        $(inputSelector).val(result.rating);
    }

    /**
     * Initialize the new risk form
     */
    function initNewRiskForm() {
        const $form = $('#newRiskForm');
        if (!$form.length) return;

        // Overall rating calculation
        $('#threatLikelihood, #threatSeverityRating').on('change', function() {
            updateOverallRatingDisplay(
                '#threatLikelihood',
                '#threatSeverityRating',
                '#overallThreatRatingDisplay',
                '#overallThreatRating'
            );
        });

        // Form submission
        $form.on('submit', function(e) {
            e.preventDefault();

            const $submitBtn = $form.find('button[type="submit"]');
            const originalText = $submitBtn.text();
            $submitBtn.prop('disabled', true).text('Creating...');

            // Collect form data
            const formData = new FormData(this);
            formData.append('action', 'submit_new_risk');
            formData.append('nonce', riskRegisterData.nonce);

            // Collect multi-select threat categories
            const selectedThreats = $('#threatCategories').val() || [];
            formData.set('threatCategories', JSON.stringify(selectedThreats));

            $.ajax({
                url: riskRegisterData.ajax_url,
                type: 'POST',
                data: formData,
                processData: false,
                contentType: false,
                success: function(response) {
                    $submitBtn.prop('disabled', false).text(originalText);

                    if (response.success) {
                        // Close modal
                        const modal = bootstrap.Modal.getInstance(document.getElementById('newRiskModal'));
                        if (modal) modal.hide();

                        // Redirect to the new risk's detail page
                        var riskId = response.data && response.data.risk_id;
                        var baseUrl = (typeof riskRegisterData !== 'undefined' && riskRegisterData.home_url) ? riskRegisterData.home_url.replace(/\/+$/, '') : '';
                        if (riskId && baseUrl) {
                            window.location.href = baseUrl + '/risk/' + riskId;
                        } else {
                            showToast('Risk created successfully', 'success');
                            refreshRiskTable();
                        }
                    } else {
                        showToast('Error: ' + (response.data.message || 'Failed to create risk'), 'error');
                    }
                },
                error: function(xhr, status, error) {
                    $submitBtn.prop('disabled', false).text(originalText);
                    console.error('Error creating risk:', error);
                    showToast('An error occurred while creating the risk', 'error');
                }
            });
        });
    }

    /**
     * Initialize the edit risk form
     */
    function initEditRiskForm() {
        const $form = $('#editRiskForm');
        if (!$form.length) return;

        // Overall rating calculation for edit form
        $('#editThreatLikelihood, #editThreatSeverityRating').on('change', function() {
            updateOverallRatingDisplay(
                '#editThreatLikelihood',
                '#editThreatSeverityRating',
                '#editOverallThreatRatingDisplay',
                '#editOverallThreatRating'
            );
        });

        // Form submission
        $form.on('submit', function(e) {
            e.preventDefault();

            const $submitBtn = $form.find('button[type="submit"]');
            const originalText = $submitBtn.text();
            $submitBtn.prop('disabled', true).text('Saving...');

            // Collect form data
            const formData = new FormData(this);
            formData.append('action', 'edit_risk');
            formData.append('nonce', riskRegisterData.nonce);

            // Collect multi-select threat categories
            const selectedThreats = $('#editThreatCategories').val() || [];
            formData.set('threatCategories', JSON.stringify(selectedThreats));

            $.ajax({
                url: riskRegisterData.ajax_url,
                type: 'POST',
                data: formData,
                processData: false,
                contentType: false,
                success: function(response) {
                    $submitBtn.prop('disabled', false).text(originalText);

                    if (response.success) {
                        // Close modal
                        const modal = bootstrap.Modal.getInstance(document.getElementById('editRiskModal'));
                        if (modal) modal.hide();

                        // Show success message
                        showToast('Risk updated successfully', 'success');

                        // Refresh table
                        refreshRiskTable();
                    } else {
                        showToast('Error: ' + (response.data.message || 'Failed to update risk'), 'error');
                    }
                },
                error: function(xhr, status, error) {
                    $submitBtn.prop('disabled', false).text(originalText);
                    console.error('Error updating risk:', error);
                    showToast('An error occurred while updating the risk', 'error');
                }
            });
        });
    }

    /**
     * Populate the edit risk form with data
     * @param {string} riskId - The risk ID to load
     */
    window.populateEditRiskForm = function(riskId) {
        // First load threat categories, then load risk data
        loadThreatCategories('#editThreatCategories', function() {
            $.ajax({
                url: riskRegisterData.ajax_url,
                type: 'POST',
                data: {
                    action: 'get_risk_data_for_edit',
                    risk_id: riskId,
                    nonce: riskRegisterData.nonce
                },
                success: function(response) {
                    if (response.success) {
                        const data = response.data;

                        // Populate form fields
                        $('#editRiskIdDisplay').val(data.riskID);
                        $('#editRiskId').val(data.riskID);
                        $('#editRiskVersion').val(data.version || '1');
                        $('#editRiskTitle').val(data.riskTitle || '');
                        $('#editRiskDescription').val(data.riskDescription || '');
                        $('#editAffectedAssets').val(data.affectedAssets || '');
                        $('#editBusinessUnit').val(data.businessUnit || '');
                        $('#editDataClassification').val(data.dataClassification || '');
                        ensureRiskOwnerSelectOption($('#editRiskOwner'), data.riskOwner || '');
                        ensureRiskOwnerSelectOption($('#editRiskAdvisor'), data.riskAdvisor || '');
                        $('#editThreatLikelihood').val(normalizeRatingForSelect(data.threatLikelihood));
                        $('#editThreatLikelihoodRationale').val(data.threatLikelihoodRationale || '');
                        $('#editThreatSeverityRating').val(normalizeRatingForSelect(data.threatSeverityRating));
                        $('#editThreatSeverityRationale').val(data.threatSeverityRationale || '');
                        $('#editRiskResponseStrategy').val(data.riskResponseStrategy || '');
                        $('#editRiskStatus').val(data.riskStatus || 'Open');
                        $('#editRiskAcceptanceNotes').val(data.riskAcceptanceNotes || '');

                        // Handle dates (strip time portion if present)
                        $('#editDateIdentified').val(data.dateIdentified ? data.dateIdentified.split(' ')[0] : '');
                        $('#editLastReviewDate').val(data.lastReviewDate ? data.lastReviewDate.split(' ')[0] : '');
                        $('#editNextReviewDate').val(data.nextReviewDate ? data.nextReviewDate.split(' ')[0] : '');

                        $('#editReviewFrequency').val(data.reviewFrequency || '');
                        $('#editAuditAssessmentSource').val(data.auditAssessmentSource || '');
                        $('#editReviewHistoryNotes').val(data.reviewHistoryNotes || '');

                        // Metadata fields
                        $('#editCreatedBy').val(data.createdBy || '');
                        $('#editCreatedDate').val(data.createdDate || '');
                        $('#editLastUpdated').val(data.lastUpdated || '');

                        // Set threat categories and trigger Select2 update
                        if (data.threatCategories && Array.isArray(data.threatCategories)) {
                            $('#editThreatCategories').val(data.threatCategories).trigger('change');
                        }

                        // Calculate and display overall rating
                        updateOverallRatingDisplay(
                            '#editThreatLikelihood',
                            '#editThreatSeverityRating',
                            '#editOverallThreatRatingDisplay',
                            '#editOverallThreatRating'
                        );

                        // Show the modal — wait until it is fully visible before
                        // initializing Select2 so dropdownParent measurements are correct.
                        const editModalEl = document.getElementById('editRiskModal');
                        $(editModalEl).one('shown.bs.modal', function() {
                            initRiskOwnerSelect2($('#editRiskOwner'));
                            initRiskOwnerSelect2($('#editRiskAdvisor'));
                        });
                        const modal = new bootstrap.Modal(editModalEl);
                        modal.show();
                    } else {
                        showToast('Error: ' + (response.data.message || 'Failed to load risk data'), 'error');
                    }
                },
                error: function(xhr, status, error) {
                    console.error('Error loading risk data:', error);
                    showToast('An error occurred while loading risk data', 'error');
                }
            });
        });
    };

    /**
     * Initialize delete risk functionality (same pattern as delete evidence modal)
     */
    function initDeleteRisk() {
        $('#confirmDeleteRiskBtn').on('click', function() {
            const riskId = $('#deleteRiskId').val();
            if (!riskId) return;

            // Show loading state
            $('#deleteRiskConfirmContent').hide();
            $('#deleteRiskLoadingContent').show();

            $.ajax({
                url: riskRegisterData.ajax_url,
                type: 'POST',
                data: {
                    action: 'delete_risk',
                    riskId: riskId,
                    nonce: riskRegisterData.nonce
                },
                success: function(response) {
                    $('#deleteRiskLoadingContent').hide();
                    $('#deleteRiskConfirmContent').show();
                    const modal = bootstrap.Modal.getInstance(document.getElementById('deleteRiskModal'));
                    if (modal) modal.hide();

                    if (response.success) {
                        showToast('Risk deleted successfully', 'success');
                        refreshRiskTable();
                    } else {
                        showToast('Error: ' + (response.data || 'Failed to delete risk'), 'error');
                    }
                },
                error: function(xhr, status, error) {
                    $('#deleteRiskLoadingContent').hide();
                    $('#deleteRiskConfirmContent').show();
                    const modal = bootstrap.Modal.getInstance(document.getElementById('deleteRiskModal'));
                    if (modal) modal.hide();
                    console.error('Error deleting risk:', error);
                    showToast('An error occurred while deleting the risk', 'error');
                }
            });
        });
    }

    /**
     * Show the delete risk confirmation modal (reset to confirm state, same as delete evidence)
     * @param {string} riskId - The risk ID
     * @param {string} riskTitle - The risk title for display
     */
    window.showDeleteRiskModal = function(riskId, riskTitle) {
        $('#deleteRiskId').val(riskId);
        $('#deleteRiskTitle').text(riskTitle || '');
        $('#deleteRiskLoadingContent').hide();
        $('#deleteRiskConfirmContent').show();
        const modal = new bootstrap.Modal(document.getElementById('deleteRiskModal'));
        modal.show();
    };

    /**
     * Refresh the risk table via AJAX
     */
    window.refreshRiskTable = function() {
        const $loader = $('#risk-table-loader');
        const $container = $('#risk-table-container');

        $loader.show();

        $.ajax({
            url: riskRegisterData.ajax_url,
            type: 'POST',
            data: {
                action: 'refresh_risk_table',
                nonce: riskRegisterData.nonce
            },
            success: function(response) {
                $loader.hide();

                if (response.success) {
                    // Destroy old simple-datatables instance if exists
                    if (risksTable) {
                        risksTable.destroy();
                        risksTable = null;
                    }

                    $container.html(response.data.html);

                    // Reinitialize simple-datatables
                    initDataTable();
                    toggleRiskRegisterHeader();

                    // Update chart data if returned (so chart view stays in sync)
                    if (response.data.chartData || response.data.businessUnitChartData) {
                        updateRiskChartData(
                            response.data.chartData,
                            response.data.businessUnitChartData
                        );
                    }
                } else {
                    console.error('Error refreshing risk table:', response.data.message);
                }
            },
            error: function(xhr, status, error) {
                $loader.hide();
                console.error('Error refreshing risk table:', error);
            }
        });
    };

    /**
     * Show a toast notification
     * @param {string} message - The message to display
     * @param {string} type - The type (success, error, warning, info)
     */
    window.showToast = function(message, type) {
        // Remove any existing toast containers
        $('.toast-container').remove();

        const bgClass = type === 'success' ? 'bg-success' :
                       type === 'error' ? 'bg-danger' :
                       type === 'warning' ? 'bg-warning' : 'bg-info';

        const toastHtml =
            '<div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 9999;">' +
                '<div class="toast align-items-center text-white ' + bgClass + '" role="alert" aria-live="assertive" aria-atomic="true">' +
                    '<div class="d-flex">' +
                        '<div class="toast-body">' + message + '</div>' +
                        '<button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast" aria-label="Close"></button>' +
                    '</div>' +
                '</div>' +
            '</div>';

        $('body').append(toastHtml);

        const $toastElement = $('.toast');
        const toast = new bootstrap.Toast($toastElement[0], { delay: 3000 });
        toast.show();

        // Remove toast container after hidden
        $toastElement.on('hidden.bs.toast', function() {
            $(this).closest('.toast-container').remove();
        });
    };

    // Initialize when document is ready
    $(document).ready(init);

})(jQuery);
